import { useTranslation } from "@redotech/redo-i18n";
import LinkExternal2 from "@redotech/redo-icons/link-external-02.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoLinkInput } from "@redotech/redo-web/arbiter-components/input/redo-link-input";
import {
  RedoTextareaInputField,
  RedoTextareaInputFieldSize,
} from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { AnimatedSkeletonBlock } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { useMemo } from "react";
import { NewFlowEditor } from "../../setting/flow-editor/new-flow-editor";
import * as resetModalCss from "./reset-modal.module.css";
import { Page, ResourceResetType, useResetModal } from "./use-reset-modal";

export const ResetModal = ({
  actionNames,
  open,
  onClose,
  onSubmitFinished,
  resource,
  flowPath,
  flowId,
  initialStep,
}: {
  actionNames?: {
    close?: string;
    reason?: string;
    resetConfirm: string;
    changeResetPointOpen: string;
    changeResetPointApply: string;
    changeResetPointCancel: string;
  };
  open: boolean;
  onClose: () => void;
  onSubmitFinished: () => void;
  resource: ResourceResetType;
  flowPath?: readonly number[];
  flowId?: string;
  initialStep?: number;
}) => {
  const { t } = useTranslation();
  const {
    texts,
    resetReason: rejectionReason,
    resetReasonDisabled,
    onResetReasonChange: onRejectionReasonChange,
    onGoToChangeResetPointPage,
    onGotoChangeResetPointPageDisabled,
    onContinue,
    onCancel,
    page,
    steps,
    flowType,
    nodePositionsArray,
    team,
    highlightedNodeId,
    currentStepId,
    onResetFlowNodeClick,
    flowTypeProps,
    continueButtonDisabled,
    cancelButtonDisabled,
    loading,
    setChangeDefaultStepSelection,
    changeDefaultStepSelection,
    linkToReturnPageLoad,
    openReturnDetails,
    rejectStepSelected,
  } = useResetModal({
    resource,
    onSubmitFinished,
    flowPath,
    flowId,
    initialStep,
  });

  const highlightedNodeIds = useMemo(
    () => new Set(highlightedNodeId ? [highlightedNodeId] : []),
    [highlightedNodeId],
  );

  return (
    <RedoModal
      data-dd-action-name={actionNames?.close}
      isOpen={open}
      onModalCloseRequested={onClose}
      primaryButton={{
        "data-dd-action-name":
          page === Page.CONFIRM
            ? actionNames?.resetConfirm
            : actionNames?.changeResetPointApply,
        text: texts.continueButton,
        size: "sm",
        disabled: continueButtonDisabled,
        onClick: onContinue,
      }}
      secondaryButton={{
        "data-dd-action-name":
          page === Page.CONFIRM
            ? actionNames?.changeResetPointOpen
            : actionNames?.changeResetPointCancel,
        text: texts.cancelButton,
        size: "sm",
        disabled: cancelButtonDisabled,
        onClick: onCancel,
      }}
      subtitle={texts.subtitle}
      title={texts.title}
    >
      {(() => {
        switch (page) {
          case Page.CONFIRM:
            return (
              <Flex dir="column" gap="xl">
                {loading ? (
                  <Flex dir="column" gap="sm">
                    <AnimatedSkeletonBlock height="20px" width="80px" />
                    <AnimatedSkeletonBlock height="40px" width="100%" />
                  </Flex>
                ) : !onGotoChangeResetPointPageDisabled ? (
                  <Flex flexDirection="column" gap="lg">
                    {flowTypeProps && (
                      <RedoSingleSelectDropdownInput
                        fullWidth
                        label={t("Flow type")}
                        options={flowTypeProps.items.map((item) => ({
                          id: item,
                          value: item,
                          type: "text",
                          text:
                            item === "claim"
                              ? t("Claim flow")
                              : t("Return flow"),
                        }))}
                        optionSelected={function (args): void {
                          flowTypeProps.setSelectedItem(args.item.value);
                        }}
                        selectedItem={{
                          id: flowTypeProps.flowType,
                          value: flowTypeProps.flowType,
                          type: "text",
                          text:
                            flowTypeProps.flowType === "claim"
                              ? t("Claim flow")
                              : t("Return flow"),
                        }}
                        size="xs"
                      />
                    )}
                    <RedoLinkInput
                      data-dd-action-name={actionNames?.changeResetPointOpen}
                      icon={<LinkExternal2 height={16} width={16} />}
                      label={texts.resetPointLabel}
                      onClick={onGoToChangeResetPointPage}
                      placeholder={texts.resetPointPlaceholder}
                      size="sm"
                      value={texts.resetPointText}
                    />
                    {rejectStepSelected && (
                      <Text fontSize="xs" textColor="error">
                        Reject steps cannot be used as a reset point. Select a
                        different step.
                      </Text>
                    )}
                  </Flex>
                ) : null}
                {linkToReturnPageLoad.value ? (
                  <Flex
                    align="center"
                    flexDirection="column"
                    gap="sm"
                    justify="center"
                  >
                    <Text fontSize="sm">{texts.linkToReturnPageText}</Text>
                    <RedoButton
                      hierarchy="link_underline"
                      onClick={openReturnDetails}
                      text={texts.openReturnPageText}
                    />
                  </Flex>
                ) : null}
                <RedoTextareaInputField
                  containerHeight="120px"
                  data-dd-action-name={actionNames?.reason}
                  disabled={resetReasonDisabled}
                  fullWidth
                  label={texts.resetReasonLabel}
                  placeholder={texts.resetReasonPlaceholder}
                  setValue={onRejectionReasonChange}
                  size={RedoTextareaInputFieldSize.SMALL}
                  value={rejectionReason}
                />
              </Flex>
            );
          case Page.CHANGE_RESET_POINT:
            return (
              <Flex dir="column" gap="2xl">
                <div className={resetModalCss.flowchartContainer}>
                  <NewFlowEditor
                    currentStepId={currentStepId}
                    flowType={flowType}
                    highlightedNodeIds={highlightedNodeIds}
                    mode="readonly"
                    nodePositionsArray={nodePositionsArray}
                    onNodeClick={onResetFlowNodeClick}
                    steps={steps}
                    team={team}
                  />
                </div>

                <RedoRadioGroup
                  label={t("Change default step")}
                  options={[
                    {
                      label: t("Change reset point for this return only"),
                      id: false,
                    },
                    {
                      label: t(
                        "Change reset point for this and all future returns",
                      ),
                      id: true,
                    },
                  ]}
                  setValue={(value: boolean) =>
                    setChangeDefaultStepSelection(value)
                  }
                  value={changeDefaultStepSelection}
                />
              </Flex>
            );

          default:
            assertNever(page);
        }
      })()}
    </RedoModal>
  );
};
